import React from 'react'
import { GuildScore } from '@/atoms/GuildScore'
import { AsH3, EyebrowMD, HeadingMD } from '@/atoms/Text'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge'
import {
  formatProjectTitleCrewMap,
  formatTitleDuration,
  getFirstTitleRating,
  TitleCrewMap,
} from '@/services/ContentCatalog'
import { FilmDetails as FilmDetailsType } from '@/types/codegen-contentful'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { CastRail } from '@/views/WatchTheatricalView/FilmDetails/CastRail'
import { DescriptorSection } from '@/views/WatchTheatricalView/FilmDetails/DescriptorSection'
import { CrewCredits } from './CrewCredits'

interface FilmDetailsComponentProps {
  filmDetails: FilmDetailsType
  slug: string
  projectType: string
  guildScore?: number
}
export function FilmDetails(props: FilmDetailsComponentProps) {
  const { t } = useTranslate('theatrical-presales')
  const { locale } = useLocale()
  const projectMeta = useProjectMeta()
  const title = projectMeta.title

  const moviePosterPath = title?.portraitTitleArt?.cloudinaryPath ?? title?.portraitBackdrop?.cloudinaryPath
  const mobileMoviePosterPath = title?.landscapeTitleArt?.cloudinaryPath ?? title?.landscapeBackdrop?.cloudinaryPath
  const rating = title?.contentRatings?.length ? getFirstTitleRating(title) : undefined
  const releaseYear = props.filmDetails?.releaseYear
  const duration = formatTitleDuration(title)
  const releaseDate = formatReleaseDate(locale, props.filmDetails, projectMeta.releaseDate)
  const description = title?.longDescription ?? title?.mediumDescription ?? title?.shortDescription
  const isSeries = props.projectType === 'series'

  return (
    <div className="grid grid-cols-1 gap-[32px] text-left lg:grid-cols-2">
      {moviePosterPath && (
        <div className="hidden lg:block">
          <img
            src={getCloudinaryImageUrl({ path: moviePosterPath, width: 620, height: 930 })}
            className="ml-auto"
            alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: projectMeta.name })}
            height={930}
            width={620}
          />
        </div>
      )}
      {mobileMoviePosterPath && (
        <div className="block lg:hidden">
          <img
            src={getCloudinaryImageUrl({ path: mobileMoviePosterPath, width: 930, height: 523 })}
            className="ml-auto"
            alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: projectMeta.name })}
            height={523}
            width={930}
          />
        </div>
      )}
      <div key="details" className="px-4 py-12 md:px-8 lg:max-w-[700px] se:px-2">
        {title && (
          <>
            <HeadingMD weight="bold" color="white" as={AsH3} className="lg:photon-heading-lg mb-4">
              {projectMeta.name}
            </HeadingMD>
            <div className="mb-4 flex items-center gap-2 lg:mb-6 lg:gap-4">
              <GuildRecommendedAge projectSlug={props.slug} iconSize={23} compact />
              {rating && rating !== 'NR' && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold">
                    {rating}
                  </EyebrowMD>
                </div>
              )}
              {releaseYear && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold">
                    {releaseYear}
                  </EyebrowMD>
                </div>
              )}
              {duration && (
                <div className="w-fit whitespace-nowrap rounded-full border-[1px] border-white px-4 py-1">
                  <EyebrowMD color="white" weight="bold" className="lowercase">
                    {duration}
                  </EyebrowMD>
                </div>
              )}
              {props.guildScore ? <GuildScore guildScore={props.guildScore} /> : null}
            </div>
            <div className="mb-10">
              <DescriptorSection
                title={isSeries ? t('seriesSynopsis', 'Series Synopsis') : t('filmSynopsis', 'Film synopsis')}
                description={description ?? ''}
              />
            </div>
          </>
        )}
        <div className="mb-8 grid grid-cols-1 gap-[32px] lg:grid-cols-2">
          {title && releaseDate && (
            <DescriptorSection title={t('releaseDate', 'Release Date')} description={releaseDate} />
          )}
          <CrewSections crew={title?.crew} />
        </div>
        <div>
          <CastRail />
        </div>
      </div>
    </div>
  )
}

export function formatReleaseDate(
  locale: string,
  filmDetails: FilmDetailsType,
  projectReleaseDate?: string | null,
): string | undefined {
  if (filmDetails?.theatricalReleaseDateOverrideText) return filmDetails.theatricalReleaseDateOverrideText
  const dateString = projectReleaseDate ?? filmDetails?.theatricalReleaseDate
  if (!dateString) return undefined
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function CrewSections(props: {
  crew?: ({ name: string; job?: string | null; department?: string | null } | null)[] | null
}) {
  const { t } = useTranslate('theatrical-presales')
  if (!props.crew?.length) return null

  const crew: TitleCrewMap = formatProjectTitleCrewMap(props.crew)

  const sections = [
    {
      title: t('filmDirectorV2', {
        count: crew['Director'].length,
        defaultValue: 'Director',
        defaultValue_other: 'Directors',
      }),
      names: crew['Director'],
    },
    {
      title: t('executiveProducerV2', {
        count: crew['Executive Producer'].length,
        defaultValue: 'Executive Producer',
        defaultValue_other: 'Executive Producers',
      }),
      names: crew['Executive Producer'],
    },
    {
      title: t('producerV2', {
        count: crew['Producer'].length,
        defaultValue: 'Producer',
        defaultValue_other: 'Producers',
      }),
      names: crew['Producer'],
    },
    {
      title: t('writer', {
        count: crew['Writer'].length,
        defaultValue: 'Writer',
        defaultValue_other: 'Writers',
      }),
      names: crew['Writer'],
    },
    {
      title: t('directorOfPhotography', {
        count: crew['Director of Photography'].length,
        defaultValue: 'Director of Photography',
        defaultValue_other: 'Directors of Photography',
      }),
      names: crew['Director of Photography'],
    },
    {
      title: t('artDirector', {
        count: crew['Art Director'].length,
        defaultValue: 'Art Director',
        defaultValue_other: 'Art Directors',
      }),
      names: crew['Art Director'],
    },
    {
      title: t('distributor', {
        count: crew['Distributor'].length,
        defaultValue: 'Distributor',
        defaultValue_other: 'Distributors',
      }),
      names: crew['Distributor'],
    },
    {
      title: t('cinematographer', {
        count: crew['Cinematographer'].length,
        defaultValue: 'Cinematographer',
        defaultValue_other: 'Cinematographers',
      }),
      names: crew['Cinematographer'],
    },
  ]

  return sections.map((section) => <CrewCredits key={section.title} names={section.names} title={section.title} />)
}
